import React from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { GhostButton } from '@ui/GhostButton';
import { Menu } from '@ui/menus/Menu';
import { getBlueprintStoreyAssignmentStartLevel } from '@shared/helpers/design/blueprint-underlay.helpers';
import { formatPdfPageHeading } from '@shared/helpers/design/pdf-page-heading.helpers';
import PdfFileIcon from '../icons/pdf-file.icon.svg?react';
import { PdfContinuousPageViewer } from './PdfContinuousPageViewer';
import { BlueprintStoreyLevelSelect } from './BlueprintStoreyLevelSelect';
import { PdfImportBuildingSelect } from './PdfImportBuildingSelect';
import {
  PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX,
  PdfImportPageThumbnail,
} from './PdfImportPageThumbnail';
import { usePdfBlueprintUnderlayDialog } from '../hooks/use-pdf-blueprint-underlay-dialog.hook';
import { PDF_IMPORT_WIZARD_STEP_COPY } from '../helpers/pdf-blueprint-import.helpers';
import {
  THUMB_CHECKBOX_COLUMN_PX,
  THUMB_CHECKBOX_GAP_PX,
  usePdfBlueprintUnderlayDialogStyles,
} from './pdf-blueprint-underlay-dialog.styles';
import {
  PDF_IMPORT_DIALOG_EXIT_MS,
  type PdfBlueprintUnderlayDialogProps,
} from './pdf-blueprint-underlay-dialog.types';

export { PDF_IMPORT_DIALOG_EXIT_MS };
export type { PdfBlueprintUnderlayDialogProps };

/**
 * Pick PDF pages, assign storey roles, and place them as Design underlays.
 * Scale is measured on the Design map after placing.
 */
export const PdfBlueprintUnderlayDialog: React.FC<
  PdfBlueprintUnderlayDialogProps
> = (props) => {
  const { classes, cx } = usePdfBlueprintUnderlayDialogStyles();
  const {
    open,
    file,
    storeys,
    buildings,
    buildingId,
    setBuildingId,
    wizardStep,
    loading,
    loadingThumbnails,
    thumbContextMenu,
    setThumbContextMenu,
    selectThumbImageRefs,
    assignThumbImageRefs,
    assignThumbHeights,
    setAssignThumbHeights,
    loadedPdf,
    thumbnails,
    focusedPageIndex,
    setFocusedPageIndex,
    scrollRequestPageIndex,
    scrollRequestId,
    thumbItemRefs,
    finishImportMode,
    selectedPages,
    selectedReadyPages,
    allPagesSelected,
    getDraft,
    toggleSelected,
    toggleSelectAllPages,
    setPageSelected,
    setAssignmentStartLevel,
    storeyLevelCount,
    focusPage,
    openThumbContextMenu,
    handleBack,
    handleContinue,
    handlePlace,
    handleCancel,
    handleDialogClose,
  } = usePdfBlueprintUnderlayDialog(props);
  const { onExited } = props;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      // Step 2 hugs the mapping rows; fullWidth would stretch past content.
      fullWidth={wizardStep !== 2 || loadingThumbnails}
      transitionDuration={{ enter: 225, exit: PDF_IMPORT_DIALOG_EXIT_MS }}
      slotProps={{
        backdrop: { className: classes.backdrop },
        transition: { onExited },

        paper: {
          className: cx(
            classes.paper,
            (loadingThumbnails || wizardStep === 1) && classes.paperStep1,
            wizardStep === 2 && !loadingThumbnails && classes.paperStep2,
          ),
        },
      }}
    >
      <DialogTitle className={classes.title}>
        <div className={classes.titleRow}>
          <div className={classes.titleText}>
            <Typography className={classes.titleHeading} component="h2">
              {PDF_IMPORT_WIZARD_STEP_COPY[wizardStep].title}
            </Typography>
            <Typography className={classes.titleSubtitle} component="p">
              {PDF_IMPORT_WIZARD_STEP_COPY[wizardStep].subtitle}
            </Typography>
          </div>
        </div>
      </DialogTitle>
      <DialogContent
        className={cx(
          classes.content,
          wizardStep === 2 && !loadingThumbnails && classes.contentStep2,
        )}
      >
        {loadingThumbnails ? (
          <div className={classes.bodyShell}>
            <div className={classes.loadingRow}>
              <CircularProgress size={20} />
              <Typography variant="body2" className={classes.mutedText}>
                Loading page previews…
              </Typography>
            </div>
          </div>
        ) : wizardStep === 1 ? (
          <div className={classes.step1Layout}>
            <div className={classes.step1Shell}>
              <div className={classes.step1Toolbar}>
                <div className={classes.step1ToolbarLeft}>
                  <Checkbox
                    checked={allPagesSelected}
                    indeterminate={
                      selectedPages.length > 0 && !allPagesSelected
                    }
                    size="small"
                    disableRipple
                    className={classes.thumbCheckbox}
                    disabled={loading || thumbnails.length === 0}
                    onChange={toggleSelectAllPages}
                    slotProps={{
                      input: {
                        'aria-label': 'Select all pages',
                      },
                    }}
                  />
                  <PdfFileIcon className={classes.fileIcon} aria-hidden />
                  <Typography
                    variant="body2"
                    className={classes.fileName}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                </div>
                <Typography className={classes.pagePosition} variant="body2">
                  {focusedPageIndex !== undefined && thumbnails.length > 0
                    ? `Page ${focusedPageIndex + 1} of ${thumbnails.length}`
                    : '—'}
                </Typography>
              </div>
              <div className={classes.step1Body}>
                <aside className={classes.leftNav}>
                  <div className={classes.thumbRail}>
                    {thumbnails.map((thumb) => {
                      const draft = getDraft(thumb.pageIndex);
                      const isSelected = draft.selected;
                      const isFocused = focusedPageIndex === thumb.pageIndex;
                      const heading = formatPdfPageHeading(
                        thumb.pageIndex,
                        thumb.pageName,
                      );
                      return (
                        <div
                          key={thumb.pageIndex}
                          ref={(node) => {
                            if (node) {
                              thumbItemRefs.current.set(thumb.pageIndex, node);
                            } else {
                              thumbItemRefs.current.delete(thumb.pageIndex);
                            }
                          }}
                          className={classes.thumbItem}
                          onContextMenu={(event) =>
                            openThumbContextMenu(event, thumb.pageIndex)
                          }
                        >
                          <PdfImportPageThumbnail
                            imageDataUrl={thumb.imageDataUrl}
                            label={heading}
                            selected={isSelected}
                            focused={isFocused}
                            leadingWidthPx={THUMB_CHECKBOX_COLUMN_PX}
                            leadingGapPx={THUMB_CHECKBOX_GAP_PX}
                            imageRef={(node) => {
                              if (node) {
                                selectThumbImageRefs.current.set(
                                  thumb.pageIndex,
                                  node,
                                );
                              } else {
                                selectThumbImageRefs.current.delete(
                                  thumb.pageIndex,
                                );
                              }
                            }}
                            leading={
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                disableRipple
                                className={classes.thumbCheckbox}
                                onChange={() => toggleSelected(thumb.pageIndex)}
                                onClick={(event) => event.stopPropagation()}
                                slotProps={{
                                  input: {
                                    'aria-label': `Select ${heading}`,
                                  },
                                }}
                              />
                            }
                            onImageClick={() => focusPage(thumb.pageIndex)}
                            onLabelClick={() => focusPage(thumb.pageIndex)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </aside>
                <div className={classes.mainPane}>
                  <div
                    className={classes.previewPane}
                    onContextMenu={(event) => {
                      if (focusedPageIndex === undefined) return;
                      openThumbContextMenu(event, focusedPageIndex);
                    }}
                  >
                    {loadedPdf && thumbnails.length > 0 ? (
                      <PdfContinuousPageViewer
                        doc={loadedPdf.doc}
                        pageCount={thumbnails.length}
                        activePageIndex={focusedPageIndex ?? 0}
                        onActivePageChange={setFocusedPageIndex}
                        scrollRequestPageIndex={scrollRequestPageIndex}
                        scrollRequestId={scrollRequestId}
                      />
                    ) : (
                      <Typography variant="body2" className={classes.mutedText}>
                        Select a page to preview
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Menu
              open={Boolean(thumbContextMenu)}
              anchorPosition={thumbContextMenu?.position}
              onClose={() => setThumbContextMenu(undefined)}
              width={180}
            >
              <Menu.Item
                disabled={
                  thumbContextMenu !== undefined &&
                  getDraft(thumbContextMenu.pageIndex).selected
                }
                onClick={() => {
                  if (thumbContextMenu === undefined) return;
                  setPageSelected(thumbContextMenu.pageIndex, true);
                  setThumbContextMenu(undefined);
                }}
              >
                Select
              </Menu.Item>
              <Menu.Item
                disabled={
                  thumbContextMenu !== undefined &&
                  !getDraft(thumbContextMenu.pageIndex).selected
                }
                onClick={() => {
                  if (thumbContextMenu === undefined) return;
                  setPageSelected(thumbContextMenu.pageIndex, false);
                  setThumbContextMenu(undefined);
                }}
              >
                Deselect
              </Menu.Item>
            </Menu>
          </div>
        ) : (
          <div className={cx(classes.bodyShell, classes.step2Shell)}>
            <div className={cx(classes.mainPane, classes.step2MainPane)}>
              <div className={classes.step2List}>
                {buildings && buildings.length > 1 && (
                  <div className={classes.buildingRow}>
                    <Typography
                      variant="body2"
                      className={classes.buildingLabel}
                    >
                      Building
                    </Typography>
                    <PdfImportBuildingSelect
                      value={buildingId}
                      buildings={buildings}
                      disabled={loading}
                      onChange={setBuildingId}
                    />
                  </div>
                )}
                <div className={classes.pageRows}>
                  {selectedPages.map((thumb) => {
                    const draft = getDraft(thumb.pageIndex);
                    const heading = formatPdfPageHeading(
                      thumb.pageIndex,
                      thumb.pageName,
                    );
                    const startLevel = getBlueprintStoreyAssignmentStartLevel(
                      draft.assignment,
                      storeys,
                    );
                    // Bitmap height — → / storey align to the image (no selection chrome).
                    const imageHeight =
                      assignThumbHeights[thumb.pageIndex] ??
                      PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX;
                    return (
                      <div key={thumb.pageIndex} className={classes.assignRow}>
                        <div className={classes.assignBody}>
                          <PdfImportPageThumbnail
                            imageDataUrl={thumb.imageDataUrl}
                            label={heading}
                            selectionChrome={false}
                            imageRef={(node) => {
                              if (node) {
                                assignThumbImageRefs.current.set(
                                  thumb.pageIndex,
                                  node,
                                );
                              } else {
                                assignThumbImageRefs.current.delete(
                                  thumb.pageIndex,
                                );
                              }
                            }}
                            onImageLoad={(event) => {
                              const height = event.currentTarget.offsetHeight;
                              setAssignThumbHeights((prev) => {
                                if (prev[thumb.pageIndex] === height) {
                                  return prev;
                                }
                                return {
                                  ...prev,
                                  [thumb.pageIndex]: height,
                                };
                              });
                            }}
                          />
                          <div
                            className={classes.assignArrowSlot}
                            style={{ height: imageHeight }}
                            aria-hidden
                          >
                            <span className={classes.assignArrow}>→</span>
                          </div>
                          <div
                            className={classes.assignShowOnSlot}
                            style={{ height: imageHeight }}
                          >
                            <div className={classes.assignShowOnCard}>
                              <BlueprintStoreyLevelSelect
                                value={startLevel}
                                maxLevel={storeyLevelCount}
                                disabled={loading}
                                fullWidth
                                onChange={(level) =>
                                  setAssignmentStartLevel(
                                    thumb.pageIndex,
                                    level,
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {/* Back / Cancel stay left on every step; forward actions sit right. */}
        <div className={classes.actionsStart}>
          {wizardStep !== 1 && (
            <GhostButton onClick={handleBack} disabled={loading}>
              Back
            </GhostButton>
          )}
          <GhostButton onClick={handleCancel} disabled={loading}>
            Cancel
          </GhostButton>
        </div>
        <div className={classes.actionsEnd}>
          {/* The count reads as a caption for the forward action. */}
          {wizardStep === 1 && (
            <Typography variant="body2" className={classes.selectionCount}>
              {selectedPages.length === 1
                ? '1 page selected'
                : `${selectedPages.length} pages selected`}
            </Typography>
          )}
          {wizardStep === 1 ? (
            <GhostButton
              onClick={handleContinue}
              disabled={
                loading || loadingThumbnails || selectedPages.length === 0
              }
              startIcon={loading ? <CircularProgress size={14} /> : undefined}
            >
              Continue
            </GhostButton>
          ) : (
            <GhostButton
              onClick={() => void handlePlace(finishImportMode)}
              disabled={loading || selectedReadyPages.length === 0}
              startIcon={loading ? <CircularProgress size={14} /> : undefined}
            >
              Finish import
            </GhostButton>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};
